import { api } from '@/lib/api/client'
import type { AuthUser } from '@/lib/auth/types'
import type {
  AuthResponse,
  GoogleLoginInput,
  LoginInput,
  RegisterInput,
  UserResponse,
} from '@/features/auth/types/auth'

export interface AuthSession {
  token: string
  user: AuthUser
}

export function toAuthUser(user: UserResponse): AuthUser {
  return {
    id: String(user.id),
    email: user.email,
    firstName: user.first_name,
    lastName: user.last_name,
    hasPassword: user.has_password,
    authProviders: user.auth_providers,
    isSystemAdmin: (user.roles ?? []).some(
      (role) => role.organization_id === null && role.workspace_id === null,
    ),
  }
}

/** `GET /auth/me` returns the signed-in user with their active roles. */
export async function getMe(): Promise<AuthUser> {
  const { data } = await api.get<{ data: UserResponse }>('/auth/me')

  return toAuthUser(data.data)
}

export function toSession({ token, user }: AuthResponse): AuthSession {
  return { token, user: toAuthUser(user) }
}

export async function login(input: LoginInput): Promise<AuthSession> {
  const { data } = await api.post<{ data: AuthResponse }>('/auth/login', input)

  return toSession(data.data)
}

export async function loginWithGoogle(input: GoogleLoginInput): Promise<AuthSession> {
  const { data } = await api.post<{ data: AuthResponse }>('/auth/google', {
    access_token: input.accessToken,
  })

  return toSession(data.data)
}

export async function registerWithGoogle(input: GoogleLoginInput): Promise<AuthSession> {
  const { data } = await api.post<{ data: AuthResponse }>('/auth/google/register', {
    access_token: input.accessToken,
  })

  return toSession(data.data)
}

export async function register(input: RegisterInput): Promise<AuthSession> {
  const { data } = await api.post<{ data: AuthResponse }>('/auth/register', {
    email: input.email,
    password: input.password,
    first_name: input.firstName,
    last_name: input.lastName,
  })

  return toSession(data.data)
}

export async function logout(): Promise<void> {
  await api.post('/auth/logout')
}
